/**
 * Confirmation dialog
 * Usage: Just wrap your clickable item (like button) with <ConfirmationDialog>
 */
import { TextField } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import React, { cloneElement, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../Modal/Modal';

type Props = {
  children: ReactElement;
  message?: string;
  messageNode?: React.ReactNode;
  title?: string;
  addTextField?: boolean;
  confirmText?: string;
  cancelText?: string;
  className?: string;
  uppercaseActions?: boolean;
  isAddLaterButton?: boolean;
  textFieldValue?: string;
  confirmProps?: ButtonProps;
  cancelProps?: ButtonProps;
  checkValidation?: () => boolean;
  onOpen?: () => void;
  onConfirmClick?: () => void;
  onCancelClick?: () => void;
};

const ConfirmationDialog = (props: Props) => {
  const {
    children,
    title,
    addTextField,
    message,
    messageNode,
    confirmText,
    cancelText,
    className,
    uppercaseActions,
    isAddLaterButton,
    textFieldValue,
    confirmProps,
    cancelProps,
    checkValidation,
    onOpen,
    onConfirmClick,
    onCancelClick,
  } = props;
  const { t } = useTranslation();

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [textField, setTextField] = useState(textFieldValue || '');

  const titleOption = title || 'components.confirmation.general';
  let confirmOption = t(confirmText || 'common.ok');
  let cancelOption = t(cancelText || 'common.cancel');

  if (uppercaseActions) {
    confirmOption = confirmOption?.toUpperCase();
    cancelOption = cancelOption?.toUpperCase();
  }

  const confirm = (event) => {
    if (children?.props?.onClick) {
      event.textField = textField;
      children.props.onClick(event);
    }
    setConfirmationOpen(false);
    setTextField(textField);
  };

  return (
    <>
      {cloneElement(children, {
        onClick: (e) => {
          e.preventDefault();

          if (checkValidation && checkValidation()) {
            return;
          }

          onOpen?.();
          setConfirmationOpen(true);
        },
      })}

      <Modal
        open={confirmationOpen}
        closeIconVisible={true}
        closeButtonVisible={false}
        closeButtonText={cancelOption}
        onClose={() => setConfirmationOpen(false)}
        title={t(titleOption)}
        fullScreen={false}
        className={className}
        actions={[
          <>
            <div className="confirmation-dialog">
              {addTextField && (
                <TextField
                  variant="standard"
                  id="tracking-number"
                  value={textField}
                  className="text-field"
                  name="trackingNumber"
                  label={t('Tracking number')}
                  onChange={(event) => {
                    setTextField(event?.target?.value);
                  }}
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              <div className="confirmation-dialog-buttons">
                <Button
                  color="primary"
                  key="confirmation-modal-action-cancel"
                  size="small"
                  className="mr-1"
                  {...confirmProps}
                  disabled={textField ? true : false}
                  onClick={(event) => {
                    onCancelClick?.();
                    setConfirmationOpen(false);
                    setTextField(textFieldValue);

                    if (isAddLaterButton) {
                      confirm(event);
                    }
                  }}
                >
                  {cancelOption}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  key="confirmation-modal-action-confirm"
                  size="small"
                  {...cancelProps}
                  onClick={(event) => {
                    onConfirmClick?.();
                    confirm(event);
                  }}
                >
                  {confirmOption}
                </Button>
              </div>
            </div>
          </>,
        ]}
      >
        {messageNode ?? <div className="text-soft" dangerouslySetInnerHTML={{ __html: t(message) }} />}
      </Modal>
    </>
  );
};

export default ConfirmationDialog;
